import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GallerySubmitPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [caption, setCaption] = useState('');
  const [word, setWord] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please choose a photo first');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('submitter_name', name);
      fd.append('submitter_email', email);
      if (caption) fd.append('caption', caption);
      if (word) fd.append('bracelet_word', word);
      await axios.post(`${API}/gallery/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setDone(true);
      toast.success('Thank you — your photo is on its way to Lindy.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="pt-28 pb-20 min-h-screen bg-[#FAF9F6]" data-testid="gallery-submit-thanks">
        <Seo title="Thank you — Photo submitted" path="/gallery/submit" />
        <div className="container-custom px-6 md:px-12 max-w-xl mx-auto text-center">
          <div className="w-14 h-14 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <Check className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-4">
            Thank you, {name.split(' ')[0]}
          </h1>
          <p className="text-[#52796F] leading-relaxed mb-8 font-serif italic">
            Your photo is in Lindy&rsquo;s inbox now. She reviews each one personally before it
            joins the studio gallery — usually within a day or two. Watch your email; she may
            write to thank you.
          </p>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 text-[#354F52] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the gallery
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 min-h-screen bg-[#FAF9F6]" data-testid="gallery-submit-page">
      <Seo
        title="Share your bracelet — Whispering Wristwear gallery"
        description="Send Lindy a photograph of yourself wearing your Whispering Wristwear bracelet — your story might join the studio gallery."
        path="/gallery/submit"
      />
      <div className="container-custom px-6 md:px-12 max-w-2xl mx-auto">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back to gallery</span>
        </Link>

        <header className="mb-10">
          <p className="text-caption text-[#84A98C] mb-2 inline-flex items-center gap-2">
            <Camera className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.5} />
            Share your bracelet
          </p>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] leading-tight mb-4">
            Be part of the studio gallery
          </h1>
          <p className="text-[#52796F] leading-relaxed font-serif italic">
            Send a photo of yourself wearing your Whispering Wristwear piece — at the beach,
            at the wedding, on the morning you needed it most. Lindy reviews each one
            personally before it appears publicly.
          </p>
        </header>

        <form
          onSubmit={submit}
          className="bg-white border border-[#CAD2C5]/40 rounded-sm p-6 space-y-5"
          data-testid="ugc-form"
        >
          <div className="space-y-2">
            <Label className="text-[#354F52]">Your photo *</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="ugc-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-[#CAD2C5] rounded-sm p-6 text-[#52796F] hover:border-[#D4AF37] hover:bg-[#FAF9F6] transition-colors text-center"
              data-testid="ugc-file-trigger"
            >
              {file ? (
                <span className="font-mono text-sm">{file.name} · {(file.size / 1024).toFixed(0)} KB</span>
              ) : (
                <span className="flex flex-col items-center gap-2">
                  <Upload className="w-6 h-6 text-[#84A98C]" strokeWidth={1.5} />
                  <span>Tap to choose a photo from your phone or computer</span>
                  <span className="text-xs text-[#84A98C]">JPG, PNG or WebP · up to 8 MB</span>
                </span>
              )}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ugc-name" className="text-[#354F52]">Your name *</Label>
              <Input id="ugc-name" value={name} onChange={(e) => setName(e.target.value)}
                     required maxLength={80} data-testid="ugc-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ugc-email" className="text-[#354F52]">Your email *</Label>
              <Input id="ugc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required maxLength={120} data-testid="ugc-email" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ugc-word" className="text-[#354F52]">
              The word on your bracelet <span className="text-[#84A98C] font-normal">(optional)</span>
            </Label>
            <Input id="ugc-word" value={word} onChange={(e) => setWord(e.target.value)}
                   placeholder="e.g. LOVE, BREATHE, MAMA" maxLength={30} data-testid="ugc-word" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ugc-caption" className="text-[#354F52]">
              A few words about the moment <span className="text-[#84A98C] font-normal">(optional)</span>
            </Label>
            <Textarea id="ugc-caption" rows={3} value={caption}
                      onChange={(e) => setCaption(e.target.value)} maxLength={240}
                      placeholder="What were you doing? Why does this piece matter to you?"
                      data-testid="ugc-caption" />
          </div>

          <p className="text-xs text-[#84A98C] italic">
            By submitting you give Whispering Wristwear permission to publish your photo on
            the public studio gallery and on social media, with credit to your first name.
          </p>

          <Button
            type="submit"
            disabled={submitting || !file || !name || !email}
            className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] w-full"
            data-testid="ugc-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Send to Lindy</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
